/**
 * Native capabilities registry for multi-version OTA. Keyed by a binary's
 * MAJOR.MINOR (`native-v*` tag); read at runtime by nativeCapabilities.adapter.ts.
 * See src/appV2/NativeAdapters/README.md for the gating rules.
 *
 * Add a capability only for new/changed METHODS on a plugin already on older
 * binaries (a brand-new plugin uses `Capacitor.isPluginAvailable()` instead). To
 * add: extend `NativeCapabilities` + `NO_NATIVE_CAPABILITIES`, set it per baseline
 * (`true` only where the binary ships it), gate on `nativeCapabilities().__FLAG__`.
 *
 * - Add a new MAJOR.MINOR row BEFORE its native release (trigger-native-build.yml enforces).
 * - Newer-than-registry → newest row; older/unknown → all-false.
 * - Capabilities are monotonic (false→true only as baselines increase; test-enforced).
 * - Rows older than the support window (MAX_BASELINES) can be deleted.
 */

/**
 * Native capabilities available in the app.
 * Booleans only, so gated code paths stay trivially analyzable.
 */
export interface NativeCapabilities {
  /** Dummy capability for initial testing purposes */
  __DUMMY_CAPABILITY__: boolean;
}

/**
 * Version string in MAJOR.MINOR format (e.g., "3.7"), matching `native-v*` release tags
 */
export type NativeBaselineVersion = string;

/**
 * Configuration mapping native baseline versions to their capabilities
 */
export type NativeCapabilitiesConfig = Record<NativeBaselineVersion, NativeCapabilities>;

/**
 * Capabilities for web, unknown-older binaries, and before runtime resolution: all off.
 */
export const NO_NATIVE_CAPABILITIES: NativeCapabilities = {
  __DUMMY_CAPABILITY__: false,
};

/**
 * Native capabilities configuration by baseline version.
 * Each version must explicitly define all capabilities.
 */
export const NATIVE_CAPABILITIES: NativeCapabilitiesConfig = {
  "3.2": {
    __DUMMY_CAPABILITY__: true,
  },
  "3.4": {
    __DUMMY_CAPABILITY__: true,
  },
  "3.5": {
    __DUMMY_CAPABILITY__: true,
  },
  "3.6": {
    __DUMMY_CAPABILITY__: true,
  },
  "3.7": {
    __DUMMY_CAPABILITY__: true,
  },
  // >>>>>>>>>>>
  // ADD MORE VERSIONS HERE
  // <<<<<<<<<<
};

function parseBaseline(version: string): [major: number, minor: number] | undefined {
  const [major, minor] = version.split(".").slice(0, 2).map(Number);
  if (!Number.isInteger(major) || !Number.isInteger(minor)) {
    return undefined;
  }
  return [major, minor];
}

/**
 * Baselines known to the registry, oldest first.
 */
export function getKnownBaselinesAscending(): NativeBaselineVersion[] {
  return Object.keys(NATIVE_CAPABILITIES).sort((a, b) => {
    const [aMajor = 0, aMinor = 0] = parseBaseline(a) ?? [];
    const [bMajor = 0, bMinor = 0] = parseBaseline(b) ?? [];
    return aMajor - bMajor || aMinor - bMinor;
  });
}

/**
 * Resolves the capabilities of an installed native binary by its marketing version
 * (e.g. "3.7.2").
 *
 * - Known MAJOR.MINOR: its registry entry.
 * - Newer than the registry (native released before the registry row landed): the newest
 *   known entry — a safe subset of what the newer binary supports.
 * - Older than the registry, or unparsable: all capabilities off (the safe direction).
 */
export function getCapabilitiesForVersion(versionName: string): NativeCapabilities {
  const parsed = parseBaseline(versionName);
  if (!parsed) {
    return NO_NATIVE_CAPABILITIES;
  }

  const baseline = `${parsed[0]}.${parsed[1]}`;
  const exact = NATIVE_CAPABILITIES[baseline];
  if (exact) {
    return exact;
  }

  const newestKnown = getKnownBaselinesAscending().at(-1);
  const newestParsed = newestKnown ? parseBaseline(newestKnown) : undefined;
  if (
    newestKnown &&
    newestParsed &&
    (parsed[0] > newestParsed[0] || (parsed[0] === newestParsed[0] && parsed[1] > newestParsed[1]))
  ) {
    return NATIVE_CAPABILITIES[newestKnown] ?? NO_NATIVE_CAPABILITIES;
  }

  return NO_NATIVE_CAPABILITIES;
}
